// PlanesMark.jsx

import React, { useId } from 'react';

import {
  PLANES_A,
  PLANES_B,
  PLANES_COLORS,
  PLANES_OPACITY,
  PLANES_PLANE,
  PLANES_RADIUS,
} from '../planesMarkGeometry';

const DEFAULT_SIZE = 28;

/**
 * PlanesMark
 *
 * `variant="brand"` (default): solid blue / green / yellow hard-seam mark.
 * `variant="solid"`: single-color SVG; planes use partial opacity; overlap is a
 * solid layer on top. Pass theme foreground so it inverts for light/dark.
 *
 * @param {number|string} [size=28] - Rendered width/height in px.
 * @param {string} [color='currentColor'] - Fill color for solid translucent planes.
 * @param {string} [clipColor] - Solid overlap fill; defaults to `color`.
 * @param {'brand'|'solid'} [variant='brand'] - Color treatment.
 * @param {string} [className=''] - Optional class on the svg.
 * @param {string} [title] - Optional accessible title (otherwise aria-hidden).
 *
 * @returns {ReactNode} PlanesMark.
 */
const PlanesMark = ({
  size = DEFAULT_SIZE,
  color = 'currentColor',
  clipColor,
  variant = 'brand',
  className = '',
  title,
  ...rest
}) => {
  const numeric = typeof size === 'number' ? size : parseFloat(String(size));
  const px = Number.isFinite(numeric) && numeric > 0 ? numeric : DEFAULT_SIZE;
  const labelled = Boolean(title);
  const uid = useId().replace(/:/g, '');
  const overlapClipId = `planes-overlap-${uid}`;
  const isBrand = variant !== 'solid';
  const overlapFill = clipColor ?? color;

  return (
    <svg
      className={['planes-mark', className].filter(Boolean).join(' ')}
      width={px}
      height={px}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={labelled ? undefined : true}
      role={labelled ? 'img' : undefined}
      {...rest}
    >
      {labelled ? <title>{title}</title> : null}
      <defs>
        <clipPath id={overlapClipId}>
          <rect
            x={PLANES_A.x}
            y={PLANES_A.y}
            width={PLANES_PLANE}
            height={PLANES_PLANE}
            rx={PLANES_RADIUS}
            ry={PLANES_RADIUS}
          />
        </clipPath>
      </defs>
      {isBrand ? (
        <g>
          <rect
            x={PLANES_A.x}
            y={PLANES_A.y}
            width={PLANES_PLANE}
            height={PLANES_PLANE}
            rx={PLANES_RADIUS}
            ry={PLANES_RADIUS}
            fill={PLANES_COLORS.a}
          />
          <rect
            x={PLANES_B.x}
            y={PLANES_B.y}
            width={PLANES_PLANE}
            height={PLANES_PLANE}
            rx={PLANES_RADIUS}
            ry={PLANES_RADIUS}
            fill={PLANES_COLORS.b}
          />
          <rect
            x={PLANES_B.x}
            y={PLANES_B.y}
            width={PLANES_PLANE}
            height={PLANES_PLANE}
            rx={PLANES_RADIUS}
            ry={PLANES_RADIUS}
            fill={PLANES_COLORS.overlap}
            clipPath={`url(#${overlapClipId})`}
          />
        </g>
      ) : (
        <g fill={color}>
          <rect
            x={PLANES_A.x}
            y={PLANES_A.y}
            width={PLANES_PLANE}
            height={PLANES_PLANE}
            rx={PLANES_RADIUS}
            ry={PLANES_RADIUS}
            opacity={PLANES_OPACITY}
          />
          <rect
            x={PLANES_B.x}
            y={PLANES_B.y}
            width={PLANES_PLANE}
            height={PLANES_PLANE}
            rx={PLANES_RADIUS}
            ry={PLANES_RADIUS}
            opacity={PLANES_OPACITY}
          />
          <rect
            x={PLANES_B.x}
            y={PLANES_B.y}
            width={PLANES_PLANE}
            height={PLANES_PLANE}
            rx={PLANES_RADIUS}
            ry={PLANES_RADIUS}
            fill={overlapFill}
            clipPath={`url(#${overlapClipId})`}
          />
        </g>
      )}
    </svg>
  );
};

PlanesMark.displayName = 'PlanesMark';

export default React.memo(PlanesMark);
